import React, { useState } from 'react';
import { Box, Button, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { MoreVert as MoreIcon } from '@mui/icons-material';

/**
 * Right-aligned action bar. The first 1-2 actions render as full buttons
 * (primary first); the rest collapse into a "More" overflow menu.
 *
 * actions: [{
 *   key, label, onClick, icon?, variant?, color?, disabled?, divider?,
 *   visibleAs?: 'button' | 'menu' (force placement, otherwise auto)
 * }]
 */
export function ActionBar({ actions = [], primaryCount = 1, sx }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const visible = actions.filter(a => a && !a.hidden);
  const buttons = visible.filter(a => a.visibleAs === 'button' || (!a.visibleAs && visible.indexOf(a) < primaryCount));
  const menuItems = visible.filter(a => !buttons.includes(a));

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
      {buttons.map((a, i) => (
        <Button
          key={a.key || i}
          onClick={a.onClick}
          startIcon={a.icon}
          variant={a.variant || (i === 0 ? 'contained' : 'outlined')}
          color={a.color || (i === 0 ? 'primary' : 'inherit')}
          size="small"
          disabled={a.disabled}
          sx={{ textTransform: 'none' }}
        >
          {a.label}
        </Button>
      ))}
      {menuItems.length > 0 && (
        <>
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {menuItems.map((a, i) => (
              <MenuItem
                key={a.key || i}
                onClick={() => { setMenuAnchor(null); a.onClick?.(); }}
                disabled={a.disabled}
                divider={a.divider}
              >
                {a.icon && <ListItemIcon sx={{ minWidth: 32, color: a.color === 'error' ? 'error.main' : 'inherit' }}>{a.icon}</ListItemIcon>}
                <ListItemText
                  primary={a.label}
                  primaryTypographyProps={{ variant: 'body2', color: a.color === 'error' ? 'error.main' : 'text.primary' }}
                />
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
}

export default ActionBar;
